import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ArrowRightOutlined } from "@ant-design/icons";
import { FaCalendarAlt, FaUsers, FaHashtag } from "react-icons/fa";
import { FaRoute } from "react-icons/fa6";
import { MdOutlineFlight } from "react-icons/md";
import { convertPrice } from "../../../../services/currencyService";

import SkeletonLoader from "../../../../Components/SkeletonLoader/SkeletonLoader";

import "../../PackagesProfile/PackagePayment/PaymentSummary.scss";
import "./OrganizedTourPaymentSummary.scss";
import { useSelectedResult } from "../../../../contexts/SelectedResultContext";
import { useAppContext } from "../../../../contexts/AppContext";
import { formatUsdDisplayInteger } from "../../../../utils/ceilUsd";
import { buildOrganizedTourPaymentViewModel } from "../../../../utils/buildOrganizedTourPaymentViewModel";
import { useOrganizedTourProfileInfo } from "../../../../hooks/useOrganizedTourProfileInfo";
import { decodeOrganizedTourDealId } from "../../../../utils/organizedTourRoutes";

const PaymentSummary = ({ our_order_id }) => {
  const { isAppLoggedIn } = useAppContext();
  const {
    ldsSupplement,
    ourPrice,
    ilsToUsd,
    setCurrentStageIndex,
    roomsAndPaxInfo,
    selectedResult,
    ourSavedLDSOrder,
  } = useSelectedResult();

  const { getCreditCardFeePercent, isGlobalConstantLoaded } = useAppContext();
  const { t } = useTranslation();
  const { dealId: routeDealIdParam } = useParams();
  const routeDealId =
    decodeOrganizedTourDealId(routeDealIdParam) ||
    selectedResult?.dealId ||
    selectedResult?.ldsDealId ||
    "";

  const orgTourInfo = useOrganizedTourProfileInfo(selectedResult, routeDealId);

  const orderId =
    our_order_id ??
    ourSavedLDSOrder?.id ??
    ourSavedLDSOrder?.ourLdsId ??
    "";

  const paymentVm = useMemo(
    () =>
      buildOrganizedTourPaymentViewModel({
        selectedResult,
        roomsAndPaxInfo,
        ourPrice,
        orgTourInfo,
        savedOrder: ourSavedLDSOrder,
        orderId,
      }),
    [
      selectedResult,
      roomsAndPaxInfo,
      ourPrice,
      orgTourInfo?.resourceUrl,
      orgTourInfo?.contentText,
      orgTourInfo?.loading,
      orgTourInfo?.error,
      ourSavedLDSOrder,
      orderId,
    ],
  );

  const finalPriceIls = ourPrice?.priceWithCreditCardFee;
  const imageResolved = Boolean(
    paymentVm.heroImage && String(paymentVm.heroImage).trim().length > 0,
  );

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!ourPrice) return;
    console.log("[ORG_TOUR_PAYMENT_VIEW_MODEL_TRACE]", {
      orderId: paymentVm.orderId || String(orderId || ""),
      displayOrderNumber: paymentVm.displayOrderNumber || null,
      tourName: paymentVm.tourName,
      finalPrice: finalPriceIls,
      passengerCount: paymentVm.passengerCount?.total ?? 0,
      hasFlights: paymentVm.hasFlights,
      hasTerms: paymentVm.hasTerms,
      imageResolved,
    });
  }, [
    paymentVm.hasFlights,
    paymentVm.hasTerms,
    paymentVm.displayOrderNumber,
    paymentVm.orderId,
    paymentVm.passengerCount?.total,
    paymentVm.tourName,
    finalPriceIls,
    imageResolved,
    orderId,
    ourPrice,
  ]);

  const supplementSummary = () => {
    const supplementArray = Object.values(ldsSupplement || {});
    const filterSupp = supplementArray.filter((supp) => supp.checked);

    const getSupplementLabel = (supp) => {
      const type = supp.type || "";
      let typeLabel = "";

      if (type === "LUGGAGE" || type === "BAGGAGE") {
        typeLabel = "מזוודות";
      } else if (type === "TROLLEY") {
        typeLabel = "תיקי יד";
      } else {
        typeLabel = t(supp.type) || supp.description || type;
      }

      let weight = "";
      if (supp.weightText) {
        weight = supp.weightText;
      } else if (supp.weight && supp.unit) {
        weight = `${supp.weight}${supp.unit}`;
      } else if (supp.orig?.supp?.params) {
        const weightParam = supp.orig.supp.params.find((p) => p.name === "weight");
        const unitParam = supp.orig.supp.params.find((p) => p.name === "unit");
        if (weightParam && unitParam) {
          weight = `${weightParam.value}${unitParam.value}`;
        }
      }

      return weight ? `${typeLabel} ${weight}` : typeLabel;
    };

    const getFlightDirection = (supp) => {
      if (supp.flight) {
        const depCity = supp.flight.depCity || "";
        const arrCity = supp.flight.arrCity || "";
        if (depCity && arrCity) {
          return ` (${depCity} → ${arrCity})`;
        }
      } else if (supp.orig?.flight) {
        const leg = Array.isArray(supp.orig.flight)
          ? supp.orig.flight[0]
          : supp.orig.flight;
        const depCity = leg?.depCity || "";
        const arrCity = leg?.arrCity || "";
        if (depCity && arrCity) {
          return ` (${depCity} → ${arrCity})`;
        }
      }
      return "";
    };

    return filterSupp.map((s, index) => {
      const flightDirection = getFlightDirection(s);
      const priceInIls = s.ourFare || 0;
      const priceInUsd = ilsToUsd ? ilsToUsd(priceInIls) : 0;
      const priceDisplay =
        priceInUsd > 0
          ? `${convertPrice(priceInIls)} (${formatUsdDisplayInteger(Number(priceInUsd))}$)`
          : convertPrice(priceInIls);

      return (
        <div key={index} className="ci-summery-fee1-wrapper">
          <label className="ci-summery-fee1-title">
            {getSupplementLabel(s)}
            {flightDirection}
          </label>
          <label id="price-commission" className="ci-summery-fee0-value">
            {priceDisplay}
          </label>
        </div>
      );
    });
  };

  const displayCreditCardFee = () => {
    return (
      <>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            margin: "4px 0",
          }}
        >
          <div className="ci-summery-fee1-title">
            עמלת כרטיס ({getCreditCardFeePercent()}%)
          </div>
          <div className="ci-summery-fee2-value">
            {convertPrice(ourPrice.creditCardFee || 0)}
          </div>
        </div>
      </>
    );
  };

  const tourPriceBase = !isAppLoggedIn()
    ? (ourPrice?.priceWithCommission || 0) + (ourPrice?.handleFee || 0)
    : ourPrice?.priceWithCommission || 0;

  const [termsExpanded, setTermsExpanded] = useState(false);

  return (
    <>
      <div className="payment-summery-wrapper organized-tour-payment-summary customer-payment-summary">
        <button
          type="button"
          className="back-to-package-button"
          onClick={() => {
            if (setCurrentStageIndex) setCurrentStageIndex(0);
          }}
        >
          <ArrowRightOutlined className="back-to-package-icon" />
          <span>חזרה לפרטי הטיול</span>
        </button>

        <div className="payment-agent-price-payment-wrapper">
          <label>{t("ordersummery.ordersummery")}</label>
        </div>

        {paymentVm.heroImage ? (
          <div className="org-tour-payment-summary__hero">
            <img src={paymentVm.heroImage} alt="" loading="lazy" />
          </div>
        ) : null}

        <div className="customer-booking-details org-tour-booking-details">
          <div className="booking-detail-item">
            <FaRoute className="booking-detail-icon" />
            <div className="booking-detail-content">
              <div className="booking-detail-label">טיול</div>
              <div className="booking-detail-value">{paymentVm.tourName}</div>
              {paymentVm.routeLabel ? (
                <div className="booking-detail-subvalue">{paymentVm.routeLabel}</div>
              ) : null}
            </div>
          </div>

          <div className="booking-detail-item">
            <FaCalendarAlt className="booking-detail-icon" />
            <div className="booking-detail-content">
              <div className="booking-detail-label">תאריכים</div>
              <div className="booking-detail-value">
                {paymentVm.dateRangeDisplay ||
                  `${paymentVm.depDateLabel || "—"} — ${paymentVm.retDateLabel || "—"}`}
              </div>
            </div>
          </div>

          <div className="booking-detail-item">
            <FaUsers className="booking-detail-icon" />
            <div className="booking-detail-content">
              <div className="booking-detail-label">נוסעים</div>
              <div className="booking-detail-value">
                {paymentVm.passengerCount?.label || "—"}
              </div>
            </div>
          </div>

          {paymentVm.displayOrderNumber ? (
            <div className="booking-detail-item">
              <FaHashtag className="booking-detail-icon" />
              <div className="booking-detail-content">
                <div className="booking-detail-label">מספר הזמנה</div>
                <div className="booking-detail-value" translate="no">
                  {paymentVm.displayOrderNumber}
                </div>
              </div>
            </div>
          ) : null}

          {paymentVm.hasFlights ? (
            <div className="booking-detail-item">
              <MdOutlineFlight className="booking-detail-icon" />
              <div className="booking-detail-content">
                <div className="booking-detail-label">טיסות</div>
                <ul className="booking-detail-value org-tour-payment-summary__flight-line">
                  {paymentVm.flightLines.map((row, idx) => (
                    <li key={`${row.segmentLabel}-${idx}`}>
                      <div>{row.segmentLabel}</div>
                      <div className="org-tour-payment-summary__flight-meta">{row.line}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}

          <div className="org-tour-payment-summary__notes">
            <div>
              <strong>ביטול והערות: </strong>
              {paymentVm.cancellationHint}
            </div>
            {orgTourInfo?.loading ? (
              <div className="tw-mt-2">
                <SkeletonLoader height={10} style={{ width: "100%" }} />
              </div>
            ) : null}
            {!orgTourInfo?.loading && paymentVm.orderNotesSnippet ? (
              <div className="tw-mt-2">{paymentVm.orderNotesSnippet}</div>
            ) : null}
            {!orgTourInfo?.loading && paymentVm.termsSnippet ? (
              <div className="tw-mt-2">
                <button
                  type="button"
                  className="org-tour-payment-summary__expand"
                  onClick={() => setTermsExpanded((v) => !v)}
                >
                  {termsExpanded ? "הסתר פירוט מהספק" : "הצג פירוט מהספק"}
                </button>
                {termsExpanded ? (
                  <p className="tw-mt-1 tw-mb-0">{paymentVm.termsSnippet}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>

        <div className="ci-summery-content-wrapper">
          <div className="ci-summery-fee1-wrapper">
            <label className="ci-summery-fee1-title">מחיר הטיול</label>
            <label id="price-commission" className="ci-summery-fee1-value">
              {ilsToUsd
                ? `${convertPrice(tourPriceBase)} (${formatUsdDisplayInteger(
                    Number(ilsToUsd(Number(tourPriceBase))),
                  )}$)`
                : convertPrice(tourPriceBase)}
            </label>
          </div>

          {supplementSummary()}

          {isAppLoggedIn() ? (
            <div className="ci-summery-fee1-wrapper">
              <label className="ci-summery-fee1-title">עמלת HR</label>
              <label id="price-commission" className="ci-summery-fee1-value">
                {convertPrice(ourPrice.agentCommission)}
              </label>
            </div>
          ) : null}

          {isAppLoggedIn() ? (
            <div className="ci-summery-fee1-wrapper">
              <label className="ci-summery-fee1-title">{t("paymentagent.handlingFee")}</label>
              <label id="price-commission" className="ci-summery-fee2-value">
                {!isGlobalConstantLoaded ? (
                  <SkeletonLoader height={10} style={{ width: 50 }} />
                ) : ilsToUsd ? (
                  `${convertPrice(ourPrice.handleFee)} (${formatUsdDisplayInteger(
                    Number(ilsToUsd(Number(ourPrice.handleFee))),
                  )}$)`
                ) : (
                  convertPrice(ourPrice.handleFee)
                )}
              </label>
            </div>
          ) : null}

          {displayCreditCardFee()}
        </div>
        <div className="ci-summery-border" />
        <div className="splitPayment-card-amount-totalprice ci-summery-topay-wrapper">
          <label className="splitPayment-card-amount-price-title ci-summery-topay">
            {t("paymentagent.priceis")}:
            <span
              id="priproblem-total"
              className="splitPayment-card-amount-price-number"
            >
              {convertPrice(ourPrice.priceWithCreditCardFee)} (
              {formatUsdDisplayInteger(
                Number(ilsToUsd(Number(ourPrice.priceWithCreditCardFee))),
              )}
              $)
            </span>
          </label>
        </div>
      </div>
    </>
  );
};

export default PaymentSummary;
